import {useState} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {AxiosError} from 'axios';
import {translations} from '../../../../../../i18n/translations';
import {Modal} from '../../../../../../components/modal/modal';
import {FlatInput} from '../../../../../../components/flat-input/flat-input';
import DeleteResourceConfirmationModal from '../../../../../../components/delete-resource-confirmation-modal';
import {useSnackBar} from '../../../../../../contexts/snack-bar/context';
import {GdprErasureRequestsRepository} from '../../../../../../repositories/gdpr-erasure-requests';
import {RightToErasureRequest} from '../../../../../../backend/interfaces/right-to-erasure-request';
import {EMAIL_REGEX} from '../../../../../../utils/constants/regex';
import {DEFAULT_FORM_VALIDATIONS} from '../../../../../../utils/constants';

interface CreateErasureRequestModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (request: RightToErasureRequest) => void;
}

type FormInputs = {
  email: string;
};

export const CreateErasureRequestModal = ({
  open,
  onClose,
  onCreated,
}: CreateErasureRequestModalProps) => {
  const {t} = useTranslation();
  const snackBarContext = useSnackBar();
  const [confirmOpened, setConfirmOpened] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: {errors},
  } = useForm<FormInputs>({
    defaultValues: {email: ''},
    ...DEFAULT_FORM_VALIDATIONS,
  });

  const handleClose = () => {
    reset();
    setConfirmOpened(false);
    onClose();
  };

  const handleContinue = () => {
    setConfirmOpened(true);
  };

  const handleConfirm = () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    GdprErasureRequestsRepository.instance
      .createErasureRequest(getValues('email').trim())
      .then((request) => {
        setIsSubmitting(false);
        onCreated(request);
        handleClose();
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(translations.gdpr_erasure_requests.success.request_created),
        });
      })
      .catch((error: AxiosError) => {
        setIsSubmitting(false);
        setConfirmOpened(false);

        // Translate known status codes
        let message: string = t(translations.gdpr_erasure_requests.errors.unknown);

        if (error.response?.status === 403) {
          message = t(translations.gdpr_erasure_requests.errors.forbidden);
        } else if (error.response?.status === 404) {
          message = t(translations.gdpr_erasure_requests.errors.user_not_found);
        }

        snackBarContext.setOfficeToastNotification({severity: 'error', message});

        console.error('🔴 Could not create erasure request.', error);
      });
  };

  return (
    <>
      <Modal
        id="create-erasure-request-modal"
        open={open && !confirmOpened}
        onClose={handleClose}
        title={t(translations.gdpr_erasure_requests.create_modal.title)}
        maxWidth="sm"
        fullWidth
        actions={[
          {
            label: t(translations.gdpr_erasure_requests.create_modal.cancel_button),
            type: 'neutral',
            onClick: handleClose,
          },
          {
            label: t(translations.gdpr_erasure_requests.create_modal.submit_button),
            type: 'nondestructive',
            buttonType: 'submit',
            onClick: handleSubmit(handleContinue),
          },
        ]}
      >
        <FlatInput
          label={t(translations.gdpr_erasure_requests.create_modal.email_label)}
          placeholder={t(translations.gdpr_erasure_requests.create_modal.email_placeholder)}
          fullWidth
          {...register('email', {
            required: {
              value: true,
              message: t(translations.gdpr_erasure_requests.create_modal.email_required),
            },
            pattern: {
              value: EMAIL_REGEX,
              message: t(translations.gdpr_erasure_requests.create_modal.email_invalid),
            },
          })}
          errors={errors.email?.message}
          required
        />
      </Modal>
      <DeleteResourceConfirmationModal
        id="confirm-erasure-request-dialog"
        open={confirmOpened}
        title={t(translations.gdpr_erasure_requests.create_modal.confirm_title)}
        description={t(translations.gdpr_erasure_requests.create_modal.confirm_description)}
        cannotBeUndoneMessage={t(
          translations.gdpr_erasure_requests.create_modal.confirm_cannot_be_undone
        )}
        confirm_label={t(translations.gdpr_erasure_requests.create_modal.confirm_button)}
        cancel_label={t(translations.gdpr_erasure_requests.create_modal.cancel_confirm_button)}
        isDeleting={isSubmitting}
        onCancel={() => setConfirmOpened(false)}
        onConfirm={handleConfirm}
      />
    </>
  );
};
